import React, { createContext, useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  normalizeBudgets,
  normalizeContracts,
  normalizeProviders,
  normalizeReceipts,
  normalizeSuppliers,
  normalizeTransactions,
} from '../lib/dataNormalization';
import type {
  Budget,
  Contract,
  Provider,
  Receipt,
  Supplier,
  Transaction,
} from '../types/data';

interface DataContextType {
  contracts: Contract[];
  transactions: Transaction[];
  providers: Provider[];
  suppliers: Supplier[];
  budgets: Budget[];
  receipts: Receipt[];
  addContract: (contract: Omit<Contract, 'id'>) => string;
  updateContract: (contractId: string, updates: Partial<Contract>) => void;
  removeContract: (contractId: string) => void;
  addTransaction: (transaction: Omit<Transaction, 'id'>) => string;
  updateTransaction: (transactionId: string, updates: Partial<Transaction>) => void;
  removeTransaction: (transactionId: string) => void;
  addProvider: (provider: Omit<Provider, 'id'>) => string;
  updateProvider: (providerId: string, updates: Partial<Provider>) => void;
  removeProvider: (providerId: string) => void;
  addSupplier: (supplier: Omit<Supplier, 'id'>) => string;
  updateSupplier: (supplierId: string, updates: Partial<Supplier>) => void;
  removeSupplier: (supplierId: string) => void;
  addBudget: (budget: Omit<Budget, 'id'>) => string;
  updateBudget: (budgetId: string, updates: Partial<Budget>) => void;
  removeBudget: (budgetId: string) => void;
  addReceipt: (receipt: Omit<Receipt, 'id'>) => string;
  updateReceipt: (receiptId: string, updates: Partial<Receipt>) => void;
  removeReceipt: (receiptId: string) => void;
}

interface AppStatePayload {
  companyName: string;
  cnpj: string;
  address: string;
  contracts: Contract[];
  transactions: Transaction[];
  providers: Provider[];
  suppliers: Supplier[];
  budgets: Budget[];
  receipts: Receipt[];
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export function DataProvider({ children }: { children: React.ReactNode }) {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    let ignore = false;

    const loadAppState = async () => {
      let loadedSuccessfully = false;
      try {
        const response = await fetch('/api/app-state');
        if (!response.ok) {
          throw new Error('Falha ao carregar estado inicial da aplicação.');
        }

        const data = await response.json() as AppStatePayload;
        if (ignore) {
          return;
        }

        setContracts(normalizeContracts(data.contracts ?? []));
        setTransactions(normalizeTransactions(data.transactions ?? []));
        setProviders(normalizeProviders(data.providers ?? []));
        setSuppliers(normalizeSuppliers(data.suppliers ?? []));
        setBudgets(normalizeBudgets(data.budgets ?? []));
        setReceipts(normalizeReceipts(data.receipts ?? []));
        loadedSuccessfully = true;
      } catch (error) {
        console.warn('Falha ao carregar dados iniciais do sistema.', error);
      } finally {
        if (!ignore && loadedSuccessfully) {
          setIsHydrated(true);
        }
      }
    };

    void loadAppState();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (!isHydrated) {
      return;
    }

    void fetch('/api/app-state/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        contracts,
        transactions,
        providers,
        suppliers,
        budgets,
        receipts,
      }),
    }).catch(() => {
      // O sistema continua funcional mesmo se uma sincronização falhar.
    });
  }, [budgets, contracts, isHydrated, providers, receipts, suppliers, transactions]);

  const addContract = (contract: Omit<Contract, 'id'>) => {
    const id = uuidv4();
    setContracts((currentContracts) => [{ ...contract, id }, ...currentContracts]);
    return id;
  };

  const updateContract = (contractId: string, updates: Partial<Contract>) => {
    setContracts((currentContracts) =>
      currentContracts.map((contract) =>
        contract.id === contractId
          ? {
              ...contract,
              ...updates,
              attachments: updates.attachments ?? contract.attachments,
            }
          : contract,
      ),
    );
  };

  const removeContract = (contractId: string) => {
    setContracts((currentContracts) => currentContracts.filter((contract) => contract.id !== contractId));
  };

  const addTransaction = (transaction: Omit<Transaction, 'id'>) => {
    const id = uuidv4();
    setTransactions((currentTransactions) => [{ ...transaction, id }, ...currentTransactions]);
    return id;
  };

  const updateTransaction = (transactionId: string, updates: Partial<Transaction>) => {
    setTransactions((currentTransactions) =>
      currentTransactions.map((transaction) =>
        transaction.id === transactionId
          ? {
              ...transaction,
              ...updates,
            }
          : transaction,
      ),
    );
  };

  const removeTransaction = (transactionId: string) => {
    setTransactions((currentTransactions) => currentTransactions.filter((transaction) => transaction.id !== transactionId));
  };

  const addProvider = (provider: Omit<Provider, 'id'>) => {
    const id = uuidv4();
    setProviders((currentProviders) => [{ ...provider, id }, ...currentProviders]);
    return id;
  };

  const updateProvider = (providerId: string, updates: Partial<Provider>) => {
    setProviders((currentProviders) =>
      currentProviders.map((provider) =>
        provider.id === providerId ? { ...provider, ...updates } : provider,
      ),
    );
  };

  const removeProvider = (providerId: string) => {
    setProviders((currentProviders) => currentProviders.filter((provider) => provider.id !== providerId));
  };

  const addSupplier = (supplier: Omit<Supplier, 'id'>) => {
    const id = uuidv4();
    setSuppliers((currentSuppliers) => [{ ...supplier, id }, ...currentSuppliers]);
    return id;
  };

  const updateSupplier = (supplierId: string, updates: Partial<Supplier>) => {
    setSuppliers((currentSuppliers) =>
      currentSuppliers.map((supplier) =>
        supplier.id === supplierId ? { ...supplier, ...updates } : supplier,
      ),
    );
  };

  const removeSupplier = (supplierId: string) => {
    setSuppliers((currentSuppliers) => currentSuppliers.filter((supplier) => supplier.id !== supplierId));
  };

  const addBudget = (budget: Omit<Budget, 'id'>) => {
    const id = uuidv4();
    setBudgets((currentBudgets) => [{ ...budget, id }, ...currentBudgets]);
    return id;
  };

  const updateBudget = (budgetId: string, updates: Partial<Budget>) => {
    setBudgets((currentBudgets) =>
      currentBudgets.map((budget) =>
        budget.id === budgetId
          ? {
              ...budget,
              ...updates,
              services: updates.services ?? budget.services,
              attachments: updates.attachments ?? budget.attachments,
            }
          : budget,
      ),
    );
  };

  const removeBudget = (budgetId: string) => {
    setBudgets((currentBudgets) => currentBudgets.filter((budget) => budget.id !== budgetId));
  };

  const addReceipt = (receipt: Omit<Receipt, 'id'>) => {
    const id = uuidv4();
    setReceipts((currentReceipts) => [{ ...receipt, id }, ...currentReceipts]);
    return id;
  };

  const updateReceipt = (receiptId: string, updates: Partial<Receipt>) => {
    setReceipts((currentReceipts) =>
      currentReceipts.map((receipt) =>
        receipt.id === receiptId
          ? {
              ...receipt,
              ...updates,
              attachments: updates.attachments ?? receipt.attachments,
            }
          : receipt,
      ),
    );
  };

  const removeReceipt = (receiptId: string) => {
    setReceipts((currentReceipts) => currentReceipts.filter((receipt) => receipt.id !== receiptId));
  };

  return (
    <DataContext.Provider
      value={{
        contracts,
        transactions,
        providers,
        suppliers,
        budgets,
        receipts,
        addContract,
        updateContract,
        removeContract,
        addTransaction,
        updateTransaction,
        removeTransaction,
        addProvider,
        updateProvider,
        removeProvider,
        addSupplier,
        updateSupplier,
        removeSupplier,
        addBudget,
        updateBudget,
        removeBudget,
        addReceipt,
        updateReceipt,
        removeReceipt,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}
